import { Navigate, Route } from 'react-router';

import DynamicImport from 'components/core/DynamicImport';
import RestrictedRoute from 'components/core/routing/RestrictedRoute';
import { SecondaryViewType } from 'containers/nestedView/interfaces';
import { CustomEntity } from 'enums/extendedEntityType';
import { RoutePath, SubRoute } from 'enums/routePath';
import { TabType } from 'enums/tabType';
import { ROOFTOPS_BASIC_PERMISSIONS } from 'utils/permissionUtils';

import { AccessLevel, EntityType, ResourceType, SortDirection } from 'store/client/graphql';

const SettingsLayout = DynamicImport(() => import('components/ui/layouts/SettingsLayout'));
const SettingsContentContainer = DynamicImport(() => import('containers/settings/containers/SettingsContentContainer'));
const MyAccountContainer = DynamicImport(() => import('containers/myAccount/MyAccountContainer'));
const GroupBulkAdjustmentsLayout = DynamicImport(
  () => import('containers/settings/views/GroupBulkAdjustmentsView/GroupBulkAdjustmentsLayout')
);
const GroupPricingSegmentsLayout = DynamicImport(
  () => import('containers/settings/views/GroupPricingSegmentsView/GroupPricingSegmentsLayout')
);
const EasyOfferSettingsLayout = DynamicImport(
  () => import('containers/settings/views/RooftopTradeInToolView/EasyOfferSettingsLayout')
);
const RooftopTradeInToolView = DynamicImport(
  () => import('containers/settings/views/RooftopTradeInToolView/RooftopTradeInToolView')
);
const RooftopValuationAdjustmentsLayout = DynamicImport(
  () => import('containers/settings/views/RooftopValuationAdjustmentsView/RooftopValuationAdjustmentsLayout')
);
const SettingsDetailsSideSheet = DynamicImport(
  () => import('containers/settings/shared/SettingsDetailsSideSheet'),
  null
);

const SettingsRoutes = () => (
  <Route
    element={
      <RestrictedRoute authorizedPermissions={null}>
        <SettingsLayout />
      </RestrictedRoute>
    }
    handle={{ pathPattern: RoutePath.SETTINGS }}
    path={RoutePath.SETTINGS}
  >
    <Route handle={{ pathPattern: `/${SubRoute.GROUP}/:groupId` }} path={`${SubRoute.GROUP}/:groupId?`}>
      <Route
        element={
          <SettingsContentContainer
            entityType={EntityType.GROUP}
            setQueryVars={({ group }) => ({ id: group?.id })}
            titleOverride={({ group }) => group?.name.value || ''}
            viewType={SecondaryViewType.DETAILS}
          />
        }
        handle={{ labelKey: 'general', pathPattern: `/${SubRoute.GENERAL}` }}
        path={SubRoute.GENERAL}
      />
      <Route
        element={
          <SettingsContentContainer
            entityType={CustomEntity.USER}
            setQueryVars={({ group }) => ({
              filter: { groupId: group?.id },
              sort: [
                { columnId: 'active', direction: SortDirection.DESCENDING },
                { columnId: 'updated', direction: SortDirection.DESCENDING },
              ],
            })}
            titleKey="users"
            viewType={SecondaryViewType.LIST}
          />
        }
        handle={{ labelKey: 'users', pathPattern: `/${SubRoute.USERS}` }}
        path={SubRoute.USERS}
      />
      <Route
        element={
          <RestrictedRoute
            authorizedPermissions={[{ level: AccessLevel.BASIC, resource: ResourceType.RETAIL_ITEMS }]}
            hideCoreLayout
            redirectPath={RoutePath.SETTINGS}
          >
            <GroupBulkAdjustmentsLayout />
          </RestrictedRoute>
        }
        handle={{
          authorizedPermissions: [{ level: AccessLevel.BASIC, resource: ResourceType.RETAIL_ITEMS }],
          labelKey: 'bulk_adjustment_other',
          pathPattern: `/${SubRoute.BULK_ADJUSTMENTS}`,
        }}
        path={SubRoute.BULK_ADJUSTMENTS}
      >
        <Route element={<SettingsDetailsSideSheet />} path=":itemId" />
      </Route>
      <Route
        element={
          <RestrictedRoute
            authorizedPermissions={[{ level: AccessLevel.ADVANCED, resource: ResourceType.ROOFTOPS }]}
            hideCoreLayout
            redirectPath={RoutePath.SETTINGS}
          >
            <GroupPricingSegmentsLayout />
          </RestrictedRoute>
        }
        handle={{
          authorizedPermissions: [{ level: AccessLevel.ADVANCED, resource: ResourceType.ROOFTOPS }],
          labelKey: 'pricing_segment_other',
          pathPattern: `/${SubRoute.PRICING_SEGMENTS}`,
        }}
        path={SubRoute.PRICING_SEGMENTS}
      >
        <Route element={<SettingsDetailsSideSheet />} path=":itemId" />
      </Route>
      <Route
        element={
          <SettingsContentContainer
            entityType={CustomEntity.GROUP_TAGS}
            setQueryVars={({ group }) => ({
              groupId: group?.id,
            })}
            titleKey="tags"
            viewType={SecondaryViewType.LIST}
          />
        }
        handle={{ labelKey: 'tags', pathPattern: `/${SubRoute.TAGS}` }}
        path={SubRoute.TAGS}
      />
    </Route>
    <Route handle={{ pathPattern: `/${SubRoute.ROOFTOP}/:rooftopId` }} path={`${SubRoute.ROOFTOP}/:rooftopId?`}>
      <Route
        element={
          <SettingsContentContainer
            entityType={CustomEntity.ROOFTOP}
            setQueryVars={({ rooftop }) => ({ id: rooftop?.id })}
            titleOverride={({ rooftop }) => rooftop?.name?.value || ''}
            viewType={SecondaryViewType.DETAILS}
          />
        }
        handle={{ labelKey: 'general', pathPattern: `/${SubRoute.GENERAL}` }}
        path={SubRoute.GENERAL}
      />
      <Route
        element={
          <SettingsContentContainer
            entityType={CustomEntity.ROOFTOP_PACKAGE}
            setQueryVars={({ rooftop }) => ({
              id: rooftop?.id,
            })}
            titleKey="rooftop_package_one"
            viewType={SecondaryViewType.DETAILS}
          />
        }
        handle={{ labelKey: 'rooftop_package_one', pathPattern: `/${SubRoute.PACKAGE}` }}
        path={SubRoute.PACKAGE}
      />
      <Route
        element={
          <SettingsContentContainer
            entityType={EntityType.LEAD_FORWARD}
            setQueryVars={({ rooftop }) => ({
              id: rooftop?.id,
            })}
            titleKey="lead_forwarding"
            viewType={SecondaryViewType.LIST}
          />
        }
        handle={{ labelKey: 'lead_forwarding', pathPattern: `/${SubRoute.LEAD_FORWARDING}` }}
        path={SubRoute.LEAD_FORWARDING}
      />
      <Route
        element={<EasyOfferSettingsLayout />}
        handle={{ labelKey: 'easy_offer', pathPattern: `/${SubRoute.TRADE_IN_TOOL}` }}
        path={SubRoute.TRADE_IN_TOOL}
      >
        <Route element={<Navigate replace to={SubRoute.DETAILS} />} index />
        <Route element={<RooftopTradeInToolView />} path={SubRoute.DETAILS} />
        <Route
          element={
            <RestrictedRoute
              authorizedPermissions={ROOFTOPS_BASIC_PERMISSIONS}
              hideCoreLayout
              redirectPath={RoutePath.SETTINGS}
            >
              <RooftopValuationAdjustmentsLayout />
            </RestrictedRoute>
          }
          handle={{
            authorizedPermissions: ROOFTOPS_BASIC_PERMISSIONS,
            labelKey: 'valuation_adjustment_other',
            pathPattern: `/${SubRoute.VALUATION_ADJUSTMENTS}`,
          }}
          path={SubRoute.VALUATION_ADJUSTMENTS}
        >
          <Route element={<SettingsDetailsSideSheet />} path=":itemId" />
        </Route>
      </Route>
      <Route
        element={
          <SettingsContentContainer
            entityType={CustomEntity.SETTINGS_ROOFTOP_DIGITAL_RETAILING_TOOL}
            setQueryVars={({ rooftop }) => ({
              id: rooftop?.id,
            })}
            titleKey="digital_retailing"
            viewType={SecondaryViewType.DETAILS}
          />
        }
        handle={{ labelKey: 'digital_retailing', pathPattern: `/${SubRoute.DIGITAL_RETAILING_TOOL}` }}
        path={SubRoute.DIGITAL_RETAILING_TOOL}
      />
      <Route
        element={
          <RestrictedRoute
            authorizedPermissions={null}
            featureFlag="retailPortalConfigurationEnabled"
            hideCoreLayout
            redirectPath={RoutePath.SETTINGS}
          >
            <SettingsContentContainer
              entityType={CustomEntity.SETTINGS_ROOFTOP_RETAIL_PORTAL}
              setQueryVars={({ rooftop }) => ({
                id: rooftop?.id,
              })}
              titleKey="retail_portal"
              viewType={SecondaryViewType.DETAILS}
            />
          </RestrictedRoute>
        }
        handle={{ labelKey: 'retail_portal', pathPattern: `/${SubRoute.RETAIL_PORTAL}` }}
        path={SubRoute.RETAIL_PORTAL}
      />
      <Route
        element={
          <SettingsContentContainer
            entityType={CustomEntity.ROOFTOP_DEPARTMENTS}
            setQueryVars={({ rooftop }) => ({
              id: rooftop?.id,
            })}
            titleKey="departments"
            viewType={SecondaryViewType.LIST}
          />
        }
        handle={{ labelKey: 'departments', pathPattern: `/${SubRoute.DEPARTMENTS}` }}
        path={SubRoute.DEPARTMENTS}
      />
      <Route
        element={
          <RestrictedRoute
            authorizedPermissions={[{ level: AccessLevel.ADVANCED, resource: ResourceType.ROOFTOPS }]}
            hideCoreLayout
            redirectPath={RoutePath.SETTINGS}
          >
            <SettingsContentContainer
              entityType={CustomEntity.ROOFTOP_INTEGRATIONS}
              setQueryVars={({ rooftop }) => ({
                filter: {
                  rooftopId: rooftop?.id,
                },
              })}
              titleKey="integration_other"
              viewType={SecondaryViewType.LIST}
            />
          </RestrictedRoute>
        }
        handle={{
          authorizedPermissions: [{ level: AccessLevel.ADVANCED, resource: ResourceType.ROOFTOPS }],
          labelKey: 'integrations',
          pathPattern: `/${SubRoute.INTEGRATIONS}`,
        }}
        path={SubRoute.INTEGRATIONS}
      />
      <Route
        element={
          <RestrictedRoute
            authorizedPermissions={[{ level: AccessLevel.BASIC, resource: ResourceType.MAILBOXES }]}
            hideCoreLayout
            redirectPath={RoutePath.SETTINGS}
          >
            <SettingsContentContainer
              entityType={EntityType.MAILBOX}
              setQueryVars={({ rooftop }) => ({
                filter: {
                  rooftopId: rooftop?.id,
                },
              })}
              titleKey="mailboxes"
              viewType={SecondaryViewType.LIST}
            />
          </RestrictedRoute>
        }
        handle={{
          authorizedPermissions: [{ level: AccessLevel.BASIC, resource: ResourceType.MAILBOXES }],
          labelKey: 'mailboxes',
          pathPattern: `/${SubRoute.MAILBOXES}`,
        }}
        path={SubRoute.MAILBOXES}
      />
      <Route
        element={
          <RestrictedRoute
            authorizedPermissions={[{ level: AccessLevel.BASIC, resource: ResourceType.CONTACTS }]}
            hideCoreLayout
            redirectPath={RoutePath.SETTINGS}
          >
            <SettingsContentContainer
              entityType={CustomEntity.ROOFTOP_CONTACTS}
              setQueryVars={({ rooftop }) => ({
                filter: {
                  archived: false,
                  rooftopId: rooftop?.id,
                },
              })}
              titleKey="contacts"
              viewType={SecondaryViewType.LIST}
            />
          </RestrictedRoute>
        }
        handle={{
          authorizedPermissions: [{ level: AccessLevel.BASIC, resource: ResourceType.CONTACTS }],
          labelKey: 'contacts',
          pathPattern: `/${SubRoute.CONTACTS}`,
        }}
        path={SubRoute.CONTACTS}
      />
    </Route>
    <Route handle={{ pathPattern: `/${SubRoute.ACCOUNT}` }} path={SubRoute.ACCOUNT}>
      <Route element={<Navigate to={SubRoute.GENERAL} />} index />
      <Route
        element={<MyAccountContainer showIndividualTab={TabType.DETAILS} titleKey="general" />}
        handle={{ labelKey: 'general', pathPattern: `/${SubRoute.GENERAL}` }}
        path={SubRoute.GENERAL}
      />
      <Route
        element={
          <RestrictedRoute
            authorizedPermissions={[{ level: AccessLevel.FULL, resource: ResourceType.USERS }]}
            hideCoreLayout
            redirectPath={`${RoutePath.SETTINGS}/${SubRoute.ACCOUNT}`}
          >
            <MyAccountContainer showIndividualTab={TabType.USER_PERMISSIONS} titleKey="permissions" />
          </RestrictedRoute>
        }
        handle={{
          authorizedPermissions: [{ level: AccessLevel.FULL, resource: ResourceType.USERS }],
          labelKey: 'permissions',
          pathPattern: `/${SubRoute.PERMISSIONS}`,
        }}
        path={SubRoute.PERMISSIONS}
      />
    </Route>
  </Route>
);
export default SettingsRoutes;
